import { useCallback, useEffect, useState } from 'react';
import { listPending } from '../data/local/outbox';
import { flush, onSyncChange } from '../data/local/sync';
import { isOnline, watchConnection } from '../data/local/status';
import { getSession } from '../data/auth';

/**
 * Estado da fila de escrita, para a tela poder mostrar o que falta subir.
 *
 * Tenta enviar ao abrir o app e sempre que a conexão volta. Não fica
 * tentando em laço: sem rede não adianta insistir, e com rede o envio ou
 * passa ou o item fica marcado esperando decisão do usuário.
 */
export const useOutbox = () => {
  const [pending, setPending] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [needsLogin, setNeedsLogin] = useState(false);

  const refresh = useCallback(async () => {
    const userId = (await getSession())?.user?.id || null;
    setPending(await listPending(userId));
  }, []);

  const sync = useCallback(async () => {
    if (!isOnline()) return;
    const userId = (await getSession())?.user?.id || null;
    const r = await flush(userId);
    if (r) setNeedsLogin(!!r.needsLogin);
    await refresh();
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => onSyncChange(({ syncing: s }) => setSyncing(!!s)), []);

  useEffect(() => watchConnection((up) => {
    setOnline(up);
    if (up) sync();
  }), [sync]);

  // Uma tentativa ao abrir, para o que ficou da sessão anterior.
  useEffect(() => { sync(); }, [sync]);

  // Com fila parada e servidor acessível, tenta de novo de tempos em tempos.
  // Cobre o caso em que a conexão nunca "mudou" do ponto de vista do sistema,
  // mas o servidor voltou a responder.
  useEffect(() => {
    if (pending.length === 0) return undefined;
    const timer = setInterval(() => { sync(); }, 60000);
    return () => clearInterval(timer);
  }, [pending.length, sync]);

  // Itens que o servidor recusou. Tentar de novo daria o mesmo erro, então
  // ficam separados: são os que precisam de alguém olhando. Sessão vencida
  // não entra aqui — aquilo se resolve entrando de novo.
  const blocked = needsLogin ? [] : pending.filter(p => p.attempts > 0 && p.lastError);

  return { pending, blocked, syncing, online, needsLogin, sync, refresh };
};
